"use client";
import Link from "next/link";
import { useEffect, useState, type FormEvent } from "react";
import { Plus, Search, Square, Users } from "lucide-react";
import { api, apiDownload, post } from "@/lib/api/client";
import { KZ_SHORT } from "@/lib/range/mappings";
import { useApi } from "@/lib/hooks/use-api";
import { date, difficultyLabels, directionLabels } from "@/lib/format";
import type {
  Analytics,
  GroupAnalytics,
  HistoryItem,
  Lab,
  PageData,
  Scenario,
  User,
} from "@/lib/types";
import { RequireAuth } from "@/components/auth";
import { useAuth } from "@/lib/auth/provider";
import { useT } from "@/lib/i18n";
import { AnalyticsView, HistoryTable } from "@/components/dashboard";
import {
  ActionButton,
  Badge,
  Button,
  DataState,
  Empty,
  Modal,
  PageHeading,
  Pagination,
  Progress,
  Stat,
} from "@/components/ui";
function AdminTabs({ active }: { active: string }) {
  return (
    <nav className="route-tabs" aria-label="Администрирование">
      {[
        ["scenarios", "Сценарии"],
        ["users", "Пользователи"],
        ["labs", "Лаборатории"],
        ["reports", "Отчёты"],
        ["mapping", "Соответствие"],
        ["server", "Сервер"],
        ["content", "Контент аудармалары"],
        ["translations", "UI аудармалар"],
      ].map(([id, name]) => (
        <Link
          key={id}
          href={`/admin/${id}`}
          className={active === id ? "selected" : ""}
        >
          {name}
        </Link>
      ))}
    </nav>
  );
}
function ScenarioEditor({
  scenario,
  close,
  saved,
}: {
  scenario?: Scenario;
  close: () => void;
  saved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const detail = useApi<Record<string, unknown>>(
    scenario ? `/admin/scenarios/${encodeURIComponent(scenario.id)}` : null,
  );
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError(undefined);
    try {
      const payload = {
        title: data.get("title"),
        description: data.get("description"),
        direction: data.get("direction"),
        difficulty: data.get("difficulty"),
        duration_minutes: Number(data.get("duration_minutes")),
        version: data.get("version"),
        type: data.get("type"),
        tasks: JSON.parse(String(data.get("tasks"))),
        scoring_rules: JSON.parse(String(data.get("scoring_rules"))),
        environment: JSON.parse(String(data.get("environment"))),
        hints: JSON.parse(String(data.get("hints"))),
        safety_settings: JSON.parse(String(data.get("safety_settings"))),
      };
      await api(
        scenario
          ? `/admin/scenarios/${encodeURIComponent(scenario.id)}`
          : "/admin/scenarios",
        { method: scenario ? "PATCH" : "POST", body: JSON.stringify(payload) },
      );
      saved();
      close();
    } catch (e) {
      setError(
        e instanceof SyntaxError
          ? "Проверьте синтаксис JSON в полях сценария."
          : (e as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <DataState
      loading={detail.loading}
      error={detail.error}
      retry={detail.reload}
    >
      <form className="form-grid" onSubmit={submit}>
        <label className="field span-two">
          Название
          <input name="title" defaultValue={scenario?.title} required />
        </label>
        <label className="field span-two">
          Описание
          <textarea
            name="description"
            defaultValue={scenario?.description}
            rows={3}
            required
          />
        </label>
        <label className="field">
          Направление
          <select name="direction" defaultValue={scenario?.direction}>
            {Object.entries(directionLabels).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Сложность
          <select name="difficulty" defaultValue={scenario?.difficulty}>
            {Object.entries(difficultyLabels).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Тип практики
          <select name="type" defaultValue={scenario?.type ?? "QUIZ"}>
            <option value="QUIZ">Тестирование</option>
            <option value="LAB">Лаборатория</option>
            <option value="INVESTIGATION">Расследование</option>
          </select>
        </label>
        <label className="field">
          Длительность, мин
          <input
            name="duration_minutes"
            type="number"
            min="1"
            defaultValue={scenario?.duration_minutes ?? 30}
            required
          />
        </label>
        <label className="field">
          Версия
          <input
            name="version"
            defaultValue={scenario?.version ?? "1.0.0"}
            required
          />
        </label>
        {[
          ["tasks", "Задания", []],
          ["scoring_rules", "Правила оценки", {}],
          ["environment", "Окружение", {}],
          ["hints", "Подсказки", []],
          ["safety_settings", "Настройки безопасности", {}],
        ].map(([name, label, fallback]) => (
          <label className="field span-two" key={String(name)}>
            {String(label)} · JSON
            <textarea
              className="mono"
              name={String(name)}
              rows={4}
              defaultValue={JSON.stringify(
                detail.data?.[String(name)] ?? fallback,
                null,
                2,
              )}
              required
            />
          </label>
        ))}
        <p className="muted span-two">
          Сохранение создаёт черновик. Проверка правил и публикация выполняются
          сервером сценариев.
        </p>
        {error && (
          <p className="form-error span-two" role="alert">
            {error}
          </p>
        )}
        <div className="modal-actions span-two">
          <Button variant="secondary" type="button" onClick={close}>
            Отмена
          </Button>
          <Button busy={busy} type="submit">
            Сохранить черновик
          </Button>
        </div>
      </form>
    </DataState>
  );
}
function ScenariosContent() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState<Scenario | "new" | null>(null);
  const request = useApi<PageData<Scenario>>(
    `/admin/scenarios?${new URLSearchParams({ search, status, page: String(page), page_size: "20" })}`,
  );
  return (
    <>
      <div className="catalog-toolbar">
        <label className="search-box">
          <Search size={18} />
          <input
            aria-label="Поиск сценариев"
            placeholder="Найти сценарий…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <select
          aria-label="Статус сценария"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Все статусы</option>
          {["DRAFT", "VALIDATED", "PUBLISHED", "DISABLED"].map((v) => (
            <option key={v}>{v}</option>
          ))}
        </select>
        <Button onClick={() => setEditing("new")}>
          <Plus size={17} />
          Создать сценарий
        </Button>
      </div>
      <DataState {...request} retry={request.reload}>
        {request.data?.items.length ? (
          <>
            <div className="panel table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Сценарий</th>
                    <th>Версия</th>
                    <th>Статус</th>
                    <th>Автор</th>
                    <th>Обновлён</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {request.data.items.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <strong>{s.title}</strong>
                      </td>
                      <td>{s.version}</td>
                      <td>
                        <Badge value={s.status} />
                      </td>
                      <td>{s.author}</td>
                      <td>{date(s.updated_at)}</td>
                      <td>
                        <div className="table-actions">
                          <Button variant="ghost" onClick={() => setEditing(s)}>
                            Изменить
                          </Button>
                          {s.status === "DRAFT" && (
                            <ActionButton
                              variant="secondary"
                              action={() =>
                                post(
                                  `/admin/scenarios/${encodeURIComponent(s.id)}/validate`,
                                )
                              }
                              onSuccess={request.reload}
                            >
                              Проверить
                            </ActionButton>
                          )}
                          {s.status === "VALIDATED" && (
                            <ActionButton
                              action={() =>
                                post(
                                  `/admin/scenarios/${encodeURIComponent(s.id)}/publish`,
                                )
                              }
                              onSuccess={request.reload}
                            >
                              Опубликовать
                            </ActionButton>
                          )}
                          {s.status === "PUBLISHED" && (
                            <ActionButton
                              variant="secondary"
                              action={() =>
                                post(
                                  `/admin/scenarios/${encodeURIComponent(s.id)}/disable`,
                                )
                              }
                              onSuccess={request.reload}
                            >
                              Отключить
                            </ActionButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              total={request.data.total}
              size={20}
              onChange={setPage}
            />
          </>
        ) : (
          <Empty
            title="Сценариев пока нет"
            text="Создайте первый черновик для проверки в Scenario Engine."
          />
        )}
      </DataState>
      <Modal
        title={editing === "new" ? "Новый сценарий" : "Редактирование сценария"}
        open={!!editing}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <ScenarioEditor
            key={editing === "new" ? "new" : editing.id}
            scenario={editing === "new" ? undefined : editing}
            close={() => setEditing(null)}
            saved={request.reload}
          />
        )}
      </Modal>
    </>
  );
}
function UserDetails({
  user,
  base = "/admin/users",
}: {
  user: User;
  base?: string;
}) {
  const history = useApi<PageData<HistoryItem>>(
    `${base}/${encodeURIComponent(user.id)}/history?page=1&page_size=100`,
  );
  return (
    <>
      <div className="profile-panel">
        <span className="avatar large">{user.name.charAt(0)}</span>
        <div>
          <h3>{user.name}</h3>
          <p>{user.email}</p>
          <div className="inline">
            <Badge value={user.role} />
            <span>{user.group ?? "Без группы"}</span>
          </div>
        </div>
      </div>
      <div className="stats-grid compact">
        <Stat label="Завершено" value={user.completed} />
        <Stat label="Средний балл" value={user.average_score} />
      </div>
      <Progress value={user.progress} label="Прогресс" />
      <h3 className="section">История результатов</h3>
      <DataState {...history} retry={history.reload}>
        <HistoryTable items={history.data?.items ?? []} />
      </DataState>
    </>
  );
}
function UserForm({
  user,
  close,
  saved,
}: {
  user?: User;
  close: () => void;
  saved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError(undefined);
    try {
      const fields = {
        name: data.get("name"),
        role: data.get("role"),
        group: data.get("group"),
        status: data.get("status"),
      };
      if (user) {
        await api(`/admin/users/${encodeURIComponent(user.id)}`, {
          method: "PATCH",
          body: JSON.stringify(fields),
        });
        const password = String(data.get("password") ?? "");
        if (password)
          await api(
            `/admin/users/${encodeURIComponent(user.id)}/password`,
            { method: "POST", body: JSON.stringify({ password }) },
          );
      } else {
        await post("/admin/users", {
          ...fields,
          email: data.get("email"),
          password: data.get("password"),
        });
      }
      saved();
      close();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="form-grid" onSubmit={submit}>
      <label className="field">
        Имя
        <input name="name" defaultValue={user?.name} required />
      </label>
      {!user && (
        <label className="field">
          Email
          <input
            name="email"
            type="email"
            required
            placeholder="student@example.com"
          />
        </label>
      )}
      <label className="field">
        Роль
        <select name="role" defaultValue={user?.role ?? "STUDENT"}>
          <option value="STUDENT">Студент</option>
          <option value="TEACHER">Преподаватель</option>
          <option value="ADMIN">Администратор</option>
        </select>
      </label>
      <label className="field">
        Группа
        <input
          name="group"
          defaultValue={user?.group ?? ""}
          placeholder="Например: КБ-21"
        />
      </label>
      <label className="field">
        Статус
        <select name="status" defaultValue={user?.status ?? "ACTIVE"}>
          <option value="ACTIVE">Активен</option>
          <option value="DISABLED">Отключён</option>
        </select>
      </label>
      <label className="field">
        {user ? "Новый пароль (пусто — без изменения)" : "Пароль"}
        <input
          name="password"
          type="text"
          minLength={8}
          placeholder={user ? "Оставьте пустым, чтобы не менять" : "Минимум 8 символов, буквы и цифры"}
          className="mono"
        />
      </label>
      {user && (
        <p className="muted span-two">
          Email: {user.email}. Сброс пароля завершает активные сессии
          пользователя.
        </p>
      )}
      {error && (
        <p className="form-error span-two" role="alert">
          {error}
        </p>
      )}
      <div className="modal-actions span-two">
        <Button variant="secondary" type="button" onClick={close}>
          Отмена
        </Button>
        <Button busy={busy} type="submit">
          {user ? "Сохранить" : "Создать"}
        </Button>
      </div>
    </form>
  );
}
function ResetPasswordForm({
  user,
  close,
  saved,
}: {
  user: User;
  close: () => void;
  saved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError(undefined);
    try {
      await api(`/admin/users/${encodeURIComponent(user.id)}/password`, {
        method: "POST",
        body: JSON.stringify({ password: data.get("password") }),
      });
      saved();
      close();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="form-grid" onSubmit={submit}>
      <label className="field span-two">
        Новый пароль
        <input
          name="password"
          type="text"
          minLength={8}
          required
          className="mono"
          placeholder="Минимум 8 символов, буквы и цифры"
        />
      </label>
      <p className="muted span-two">
        Активные сессии {user.name} будут завершены — войдите с новым
        паролем.
      </p>
      {error && (
        <p className="form-error span-two" role="alert">
          {error}
        </p>
      )}
      <div className="modal-actions span-two">
        <Button variant="secondary" type="button" onClick={close}>
          Отмена
        </Button>
        <Button busy={busy} type="submit">
          Сбросить пароль
        </Button>
      </div>
    </form>
  );
}
function UsersContent() {
  const { user: me } = useAuth();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [group, setGroup] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<User>();
  const [editing, setEditing] = useState<User | "new" | null>(null);
  const [resetting, setResetting] = useState<User>();
  const [deleting, setDeleting] = useState<User>();
  const request = useApi<PageData<User>>(
    `/admin/users?${new URLSearchParams({ search, role, group, status, page: String(page), page_size: "20" })}`,
  );
  return (
    <>
      <div className="catalog-toolbar">
        <label className="search-box">
          <Search size={18} />
          <input
            aria-label="Поиск пользователей"
            placeholder="Имя или email…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <select
          aria-label="Роль"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Все роли</option>
          {["STUDENT", "TEACHER", "ADMIN"].map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
        <input
          className="filter-input"
          aria-label="Группа"
          placeholder="Группа"
          value={group}
          onChange={(e) => {
            setGroup(e.target.value);
            setPage(1);
          }}
        />
        <select
          aria-label="Статус пользователя"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Все статусы</option>
          <option value="ACTIVE">Активен</option>
          <option value="DISABLED">Отключён</option>
        </select>
        <Button onClick={() => setEditing("new")}>
          <Plus size={17} />
          Создать пользователя
        </Button>
      </div>
      <DataState {...request} retry={request.reload}>
        {request.data?.items.length ? (
          <>
            <div className="panel table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Пользователь</th>
                    <th>Роль</th>
                    <th>Группа</th>
                    <th>Статус</th>
                    <th>Прогресс</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {request.data.items.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <strong>{u.name}</strong>
                        <small className="table-subtitle">{u.email}</small>
                      </td>
                      <td>
                        <select
                          className="inline-role"
                          aria-label={`Роль: ${u.name}`}
                          value={u.role}
                          disabled={u.id === me?.id}
                          onChange={async (e) => {
                            try {
                              await api(`/admin/users/${encodeURIComponent(u.id)}`, {
                                method: "PATCH",
                                body: JSON.stringify({ role: e.target.value }),
                              });
                              request.reload();
                            } catch (err) {
                              alert((err as Error).message);
                              request.reload();
                            }
                          }}
                        >
                          <option value="STUDENT">Студент</option>
                          <option value="TEACHER">Преподаватель</option>
                          <option value="COURSE_AUTHOR">Автор курса</option>
                          <option value="ADMIN">Администратор</option>
                        </select>
                      </td>
                      <td>{u.group ?? "—"}</td>
                      <td>
                        <button
                          className={`status-toggle ${u.status.toLowerCase()}`}
                          title={u.status === "ACTIVE" ? "Отключить" : "Включить"}
                          onClick={async () => {
                            if (u.id === me?.id) {
                              alert("Нельзя изменить статус собственного аккаунта.");
                              return;
                            }
                            try {
                              await api(`/admin/users/${encodeURIComponent(u.id)}`, {
                                method: "PATCH",
                                body: JSON.stringify({
                                  status: u.status === "ACTIVE" ? "DISABLED" : "ACTIVE",
                                }),
                              });
                              request.reload();
                            } catch (err) {
                              alert((err as Error).message);
                            }
                          }}
                        >
                          {u.status === "ACTIVE" ? "● Активен" : "○ Отключён"}
                        </button>
                      </td>
                      <td>
                        <Progress value={u.progress} label="Выполнено" />
                      </td>
                      <td>
                        <div className="table-actions">
                          <Button variant="ghost" onClick={() => setSelected(u)}>
                            Подробнее
                          </Button>
                          <Button variant="ghost" onClick={() => setEditing(u)}>
                            Изменить
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setResetting(u)}
                          >
                            Пароль
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setDeleting(u)}
                          >
                            Удалить
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              total={request.data.total}
              size={20}
              onChange={setPage}
            />
          </>
        ) : (
          <Empty
            title={
              request.error ? "Пользователи не найдены" : "Пользователей пока нет"
            }
            text={
              request.error
                ? "Измените фильтры или создайте пользователя."
                : "Создайте первого пользователя или дождитесь регистрации."
            }
          />
        )}
      </DataState>
      <Modal
        title="Профиль пользователя"
        open={!!selected}
        onClose={() => setSelected(undefined)}
      >
        {selected && <UserDetails key={selected.id} user={selected} />}
      </Modal>
      <Modal
        title={editing === "new" ? "Новый пользователь" : "Редактирование пользователя"}
        open={!!editing}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <UserForm
            key={editing === "new" ? "new" : editing.id}
            user={editing === "new" ? undefined : editing}
            close={() => setEditing(null)}
            saved={request.reload}
          />
        )}
      </Modal>
      <Modal
        title={resetting ? `Сброс пароля: ${resetting.name}` : "Сброс пароля"}
        open={!!resetting}
        onClose={() => setResetting(undefined)}
      >
        {resetting && (
          <ResetPasswordForm
            key={resetting.id}
            user={resetting}
            close={() => setResetting(undefined)}
            saved={request.reload}
          />
        )}
      </Modal>
      <Modal
        title="Удалить пользователя?"
        open={!!deleting}
        onClose={() => setDeleting(undefined)}
      >
        <p>
          {deleting?.name} ({deleting?.email}) будет удалён вместе с
          сессиями и результатами. Действие нельзя отменить.
        </p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setDeleting(undefined)}>
            Отмена
          </Button>
          <ActionButton
            variant="danger"
            action={async () => {
              await api(`/admin/users/${encodeURIComponent(deleting!.id)}`, {
                method: "DELETE",
              });
            }}
            onSuccess={() => {
              setDeleting(undefined);
              request.reload();
            }}
          >
            Удалить
          </ActionButton>
        </div>
      </Modal>
    </>
  );
}
function LabDetails({ lab, onReload }: { lab: Lab; onReload: () => void }) {
  const health = useApi<{
    machines?: { name: string; role: string; status: string; cpu_pct: number; ram_mb: number; http_ok: boolean | null }[];
  }>(`/admin/labs/${encodeURIComponent(lab.id)}/health`);
  const events = useApi<{
    event: string;
    reason: string;
    detail: string;
    created_at: string;
  }[]>(`/admin/labs/${encodeURIComponent(lab.id)}/events`);
  const machines = health.data?.machines ?? [];
  const eventItems = events.data ?? [];
  return (
    <div className="stack">
      <h3>{lab.scenario}</h3>
      <div className="inline">
        <Badge value={lab.status} />
        <span>Студент: {lab.student}</span>
      </div>
      <p>
        Лаборатория: <code>{lab.id}</code>
      </p>
      <p>
        Session ID: <code>{lab.session_id}</code>
      </p>
      {lab.error && <p className="form-error">{lab.error}</p>}
      {health.data && (
        <>
          <h4>Машины (health check)</h4>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Машина</th>
                  <th>Роль</th>
                  <th>Статус</th>
                  <th>CPU</th>
                  <th>RAM</th>
                  <th>HTTP</th>
                </tr>
              </thead>
              <tbody>
                {machines.map((m) => (
                  <tr key={m.name}>
                    <td>
                      <strong>{m.name}</strong>
                    </td>
                    <td>{m.role}</td>
                    <td>
                      <Badge
                        value={
                          m.status === "UP"
                            ? "RUNNING"
                            : m.status === "DOWN"
                              ? "ERROR"
                              : "CREATING"
                        }
                      />
                    </td>
                    <td>{m.cpu_pct}%</td>
                    <td>{m.ram_mb} МБ</td>
                    <td>
                      {m.http_ok === null ? "—" : m.http_ok ? "OK" : "DOWN"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {eventItems.length > 0 && (
        <>
          <h4>Audit-события</h4>
          <ul className="plain-list">
            {eventItems.map((e, i) => (
              <li key={i}>
                <code>{e.event}</code>
                {e.reason ? ` (${e.reason})` : ""} — {date(e.created_at)}
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="modal-actions">
        <ActionButton
          variant="secondary"
          action={async () => {
            await post(`/admin/labs/${encodeURIComponent(lab.id)}/reset`);
            onReload();
          }}
        >
          Сбросить
        </ActionButton>
        <ActionButton
          action={async () => {
            await post(`/admin/labs/${encodeURIComponent(lab.id)}/stop`);
            onReload();
          }}
        >
          Стоп
        </ActionButton>
        <ActionButton
          variant="secondary"
          action={async () => {
            await post(`/admin/labs/${encodeURIComponent(lab.id)}/quarantine`, {
              reason: "Администратор шешімі",
            });
            onReload();
          }}
        >
          Карантин
        </ActionButton>
        <ActionButton
          variant="danger"
          action={async () => {
            await api(`/admin/labs/${encodeURIComponent(lab.id)}`, {
              method: "DELETE",
            });
            onReload();
          }}
        >
          Уничтожить
        </ActionButton>
      </div>
      <Link
        href={`/practice/${encodeURIComponent(lab.session_id)}`}
        className="button secondary"
      >
        Открыть сессию
      </Link>
    </div>
  );
}
/** Арена maintenance: уақытша жабу/ашу (ADMIN). */
function ArenaStatusCard() {
  const t = useT();
  const state = useApi<{ closed: boolean; note: string }>("/admin/arena");
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!state.data) return;
    setBusy(true);
    try {
      await post("/admin/arena", { closed: !state.data.closed });
      state.reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="card arena-toggle"
      style={{
        borderColor: state.data?.closed ? "rgba(244, 63, 94, 0.4)" : "rgba(22, 163, 74, 0.35)",
        marginBottom: 14,
      }}
    >
      <div className="between">
        <div>
          <strong>
            {t("admin.arena_title")}:{" "}
            {state.data?.closed ? (
              <span style={{ color: "#f43f5e" }}>{t("admin.arena_closed")}</span>
            ) : (
              <span style={{ color: "#22c55e" }}>{t("admin.arena_open")}</span>
            )}
          </strong>
          <div className="muted small">{state.data?.note}</div>
        </div>
        <Button
          variant={state.data?.closed ? "primary" : "ghost"}
          onClick={toggle}
          disabled={state.loading || busy}
        >
          {state.data?.closed ? t("admin.arena_open_btn") : t("admin.arena_close_btn")}
        </Button>
      </div>
    </div>
  );
}

// LMS/аудит: журнал команд Kali (поведенческое оценивание) для лаборатории.
function AuditLogModal({
  lab,
  onClose,
}: {
  lab?: Lab;
  onClose: () => void;
}) {
  const request = useApi<{ run_id: string; commands: { ts: string; cmd: string }[] }>(
    lab ? `/admin/labs/${encodeURIComponent(lab.id)}/audit` : null,
  );
  return (
    <Modal
      title={lab ? `Аудит команд · ${lab.id}` : "Аудит"}
      open={!!lab}
      onClose={onClose}
    >
      <DataState {...request} retry={request.reload}>
        {request.data && (
          <>
            <p className="muted" style={{ marginBottom: 10 }}>
              {request.data.commands.length
                ? `Команд: ${request.data.commands.length}. Лог пишется хуком в терминале студента (Kali) и используется поведенческим оцениванием.`
                : "Лог команд пуст: студент ещё не выполнял команд или лаборатория без terminal-audit."}
            </p>
            {request.data.commands.length > 0 && (
              <div className="table-wrap" style={{ maxHeight: 420, overflowY: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 170 }}>Время (UTC)</th>
                      <th>Команда</th>
                    </tr>
                  </thead>
                  <tbody>
                    {request.data.commands.map((c, i) => (
                      <tr key={i}>
                        <td className="muted" style={{ whiteSpace: "nowrap" }}>
                          {c.ts.replace("T", " ").replace("Z", "")}
                        </td>
                        <td>
                          <code>{c.cmd}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DataState>
    </Modal>
  );
}

function LabsContent() {
  const [selected, setSelected] = useState<Lab>();
  const [stop, setStop] = useState<Lab>();
  const [auditLab, setAuditLab] = useState<Lab>();
  const [page, setPage] = useState(1);
  const request = useApi<PageData<Lab>>(
    `/admin/labs?page=${page}&page_size=20`,
    10000,
  );
  return (
    <>
      <ArenaStatusCard />
      <DataState
        loading={!request.data && request.loading}
        error={request.error}
        retry={request.reload}
      >
        {request.data?.items.length ? (
          <>
            <div className="panel table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Студент / Сценарий</th>
                    <th>Session ID / Lab</th>
                    <th>Статус</th>
                    <th>Создана</th>
                    <th>Истекает</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {request.data.items.map((lab) => (
                    <tr key={lab.id}>
                      <td>
                        <strong>{lab.student}</strong>
                        <small className="table-subtitle">{lab.scenario}</small>
                      </td>
                      <td>
                        <code>{lab.session_id}</code>
                        <small className="table-subtitle">{lab.id}</small>
                      </td>
                      <td>
                        <Badge value={lab.status} />
                      </td>
                      <td>{date(lab.created_at)}</td>
                      <td>
                        {date(lab.expires_at)}
                        <small className="table-subtitle">
                          {new Date(lab.expires_at).toLocaleTimeString("ru-RU")}
                        </small>
                      </td>
                      <td>
                        <div className="table-actions">
                          <Button
                            variant="ghost"
                            onClick={() => setSelected(lab)}
                          >
                            Подробнее
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setAuditLab(lab)}
                          >
                            Аудит
                          </Button>
                          <Button
                            variant="danger"
                            disabled={[
                              "DESTROYED",
                              "STOPPING",
                              "STOPPED",
                            ].includes(lab.status)}
                            onClick={() => setStop(lab)}
                          >
                            <Square size={13} />
                            Стоп
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              total={request.data.total}
              size={20}
              onChange={setPage}
            />
          </>
        ) : (
          <Empty title="Активных лабораторий нет" />
        )}
      </DataState>
      <AuditLogModal lab={auditLab} onClose={() => setAuditLab(undefined)} />
      <Modal
        title="Детали лаборатории"
        open={!!selected}
        onClose={() => setSelected(undefined)}
      >
        {selected && (
          <LabDetails
            key={selected.id}
            lab={selected}
            onReload={request.reload}
          />
        )}
      </Modal>
      <Modal
        title="Остановить лабораторию?"
        open={!!stop}
        onClose={() => setStop(undefined)}
      >
        <p>
          Рабочая среда пользователя {stop?.student} будет остановлена сервером.
        </p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setStop(undefined)}>
            Отмена
          </Button>
          <ActionButton
            variant="danger"
            action={() =>
              post(`/admin/labs/${encodeURIComponent(stop!.id)}/stop`)
            }
            onSuccess={() => {
              setStop(undefined);
              request.reload();
            }}
          >
            Остановить
          </ActionButton>
        </div>
      </Modal>
    </>
  );
}
const LOCALE_LABELS: Record<string, string> = {
  kk: "Қазақша",
  ru: "Русский",
  en: "English",
  ko: "한국어",
};
interface LocCompleteness {
  [entityType: string]: {
    total: number;
    locales: Record<
      string,
      { DRAFT: number; REVIEW: number; APPROVED: number; missing: number }
    >;
  };
}
interface LocRow {
  id: string;
  entity_type: string;
  entity_id: string;
  locale: string;
  title: string | null;
  translation_status: string;
  updated_at: string;
}
function TranslationsContent() {
  const comp = useApi<LocCompleteness>("/content/localizations/completeness");
  const rows = useApi<{ items: LocRow[] }>("/content/localizations");
  const [busy, setBusy] = useState<string>();
  const approve = async (id: string) => {
    setBusy(id);
    try {
      await post(`/content/localizations/${id}/review`, { action: "approve" });
      rows.reload();
      comp.reload();
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <div className="stack">
      <div className="panel">
        <h2>Аударма толықтығы (4 тіл)</h2>
        <p className="panel-sub">
          Spec §9.7: әр locale бойынша мақұлданған (APPROVED) / қарауда (REVIEW) /
          жоба (DRAFT) / жоқ. Түпнұсқа тіл — ru.
        </p>
        <DataState loading={comp.loading} error={comp.error} retry={comp.reload}>
          {comp.data && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Контент</th>
                    <th>Барлығы</th>
                    {Object.keys(LOCALE_LABELS).map((l) => (
                      <th key={l}>{LOCALE_LABELS[l]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(comp.data).map(([type, d]) => (
                    <tr key={type}>
                      <td>
                        <strong>{type}</strong>
                      </td>
                      <td>{d.total}</td>
                      {Object.keys(LOCALE_LABELS).map((l) => {
                        const c = d.locales[l];
                        return (
                          <td key={l}>
                            {c ? (
                              <>
                                <strong style={{ color: c.APPROVED === d.total ? "#067647" : undefined }}>
                                  {c.APPROVED}
                                </strong>
                                "/{d.total}"
                                {c.REVIEW > 0 && (
                                  <small> (REVIEW {c.REVIEW})</small>
                                )}
                                {c.DRAFT > 0 && <small> (DRAFT {c.DRAFT})</small>}
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DataState>
      </div>
      <div className="panel">
        <h2>Локализация жазбалары</h2>
        <DataState loading={rows.loading} error={rows.error} retry={rows.reload}>
          {!rows.loading && !rows.error && !rows.data?.items.length && (
            <p className="panel-sub">Жазбалар жоқ — контент аударылғаннан кейін осында көрінеді.</p>
          )}
          {rows.data && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Түрі</th>
                    <th>Атауы</th>
                    <th>Тіл</th>
                    <th>Күйі</th>
                    <th>Жаңартылды</th>
                    <th>Әрекет</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data.items.map((r) => (
                    <tr key={r.id}>
                      <td>{r.entity_type}</td>
                      <td>{r.title ?? "—"}</td>
                      <td>{LOCALE_LABELS[r.locale] ?? r.locale}</td>
                      <td>
                        <Badge value={r.translation_status === "APPROVED" ? "PUBLISHED" : "DRAFT"} />
                      </td>
                      <td>{date(r.updated_at)}</td>
                      <td>
                        {r.translation_status !== "APPROVED" && (
                          <Button
                            variant="secondary"
                            busy={busy === r.id}
                            onClick={() => approve(r.id)}
                          >
                            Мақұлдау
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}
/* ============================================================
 * Контент аудармалары (PHASE 2/3): курс/модуль/сабақ/қадам × kk/ru/en/ko
 * Статус-жол: MISSING → DRAFT → AI_TRANSLATED → HUMAN_REVIEWED → APPROVED → PUBLISHED
 * ============================================================ */
interface CtInvCell {
  status: string;
  title: string | null;
  updated_at: string | null;
}
interface CtInvItem {
  kind: string;
  id: string;
  title: string;
  locales: Record<string, CtInvCell>;
}
const CT_STATUS_LABELS: Record<string, string> = {
  MISSING: "жоқ",
  DRAFT: "жоба",
  AI_TRANSLATED: "AI жоба",
  HUMAN_REVIEWED: "қарап шығылды",
  APPROVED: "мақұл",
  PUBLISHED: "нәтиже",
};

function ContentTranslations() {
  const inv = useApi<{ items: CtInvItem[]; locales: string[] }>(
    "/admin/content/inventory",
  );
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{
    kind: string;
    id: string;
    locale: string;
  } | null>(null);

  const rows = (inv.data?.items ?? []).filter(
    (r) =>
      (kind === "all" || r.kind === kind) &&
      (!q.trim() || r.title.toLowerCase().includes(q.trim().toLowerCase())),
  );
  const counts = (() => {
    const c = { ru: 0, en: 0, ko: 0 };
    for (const r of inv.data?.items ?? [])
      for (const l of ["ru", "en", "ko"]) {
        const st = r.locales[l]?.status;
        if (st === "APPROVED" || st === "PUBLISHED" || st === "HUMAN_REVIEWED")
          c[l as "ru" | "en" | "ko"] += 1;
      }
    return c;
  })();

  return (
    <div className="stack">
      <div className="panel">
        <h2>Оқу контенті аудармалары</h2>
        <p className="panel-sub">
          Көздік тіл — қазақша (kodтағы content.ts). Төмендегі жоспарлар
          ru/en/ko аудармаларын басқарады. Студентке тек HUMAN_REVIEWED және
          жоғары статус көрінеді; аударылмаған жерде қазақша түпнұсқа ( белгімен)
          көрсетіледі. Машина аудармасы runtime-да қолданылмайды.
        </p>
        <div className="inline" style={{ gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 160 }}>
            <option value="all">Барлығы</option>
            <option value="course">Курстар</option>
            <option value="module">Модульдер</option>
            <option value="lesson">Сабақтар</option>
            <option value="step">Қадамдар</option>
          </select>
          <input className="input" placeholder="Іздеу…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} />
          <span className="muted" style={{ marginLeft: "auto" }}>
            Мақұл: RU <strong>{counts.ru}</strong> · EN <strong>{counts.en}</strong> · KO <strong>{counts.ko}</strong>
          </span>
        </div>
        <DataState loading={inv.loading} error={inv.error} retry={inv.reload}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Контент (kk)</th>
                  <th>ID</th>
                  <th>КK</th>
                  <th>RU</th>
                  <th>EN</th>
                  <th>KO</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.kind}:${r.id}`}>
                    <td>
                      <strong>{r.title}</strong>
                      <div className="muted" style={{ fontSize: 11 }}>{r.kind}</div>
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{r.id}</td>
                    {["kk", "ru", "en", "ko"].map((l) => {
                      const cell = r.locales[l];
                      const editable = l !== "kk";
                      return (
                        <td key={l}>
                          <button
                            type="button"
                            className={`ct-cell ${editable ? "editable" : ""}`}
                            data-status={cell?.status ?? "MISSING"}
                            disabled={!editable}
                            title={editable ? "Ашу / аудару" : "Көздік тіл (кодта)"}
                            onClick={() => editable && setEditing({ kind: r.kind, id: r.id, locale: l })}
                          >
                            {CT_STATUS_LABELS[cell?.status ?? "MISSING"] ?? cell?.status}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <Empty title="Ештеңе табылмады" />}
          </div>
        </DataState>
      </div>
      {editing && (
        <ContentEditor
          kind={editing.kind}
          id={editing.id}
          locale={editing.locale}
          onClose={() => setEditing(null)}
          onSaved={inv.reload}
        />
      )}
    </div>
  );
}

interface CtDetail {
  kind: string;
  id: string;
  base_title: string | null;
  base_payload: Record<string, unknown>;
  locales: Record<
    string,
    { status: string; title: string | null; payload: Record<string, unknown> | null; updated_at: string | null }
  >;
}

function ContentEditor({
  kind,
  id,
  locale,
  onClose,
  onSaved,
}: {
  kind: string;
  id: string;
  locale: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const detail = useApi<CtDetail>(
    `/admin/content/translations?kind=${kind}&id=${encodeURIComponent(id)}&locale=${locale}`,
  );
  const [title, setTitle] = useState("");
  const [payloadText, setPayloadText] = useState("{}");
  const [status, setStatus] = useState("DRAFT");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (!detail.data || init) return;
    const cell = detail.data.locales[locale];
    setTitle(cell?.title ?? detail.data.base_title ?? "");
    setPayloadText(JSON.stringify(cell?.payload ?? detail.data.base_payload ?? {}, null, 2));
    setStatus(cell?.status ?? "DRAFT");
    setInit(true);
  }, [detail.data, locale, init]);

  async function save() {
    setBusy(true);
    setError(undefined);
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(payloadText || "{}");
    } catch {
      setError("Payload JSON-ін тексеріңіз.");
      setBusy(false);
      return;
    }
    try {
      await post("/admin/content/translations", { kind, id, locale, title, payload, status });
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Бұл аударманы жою?")) return;
    setBusy(true);
    try {
      await api(`/admin/content/translations?kind=${kind}&id=${encodeURIComponent(id)}&locale=${locale}`, { method: "DELETE" });
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const statusOptions = ["DRAFT", "AI_TRANSLATED", "HUMAN_REVIEWED", "APPROVED", "PUBLISHED"];

  return (
    <Modal title={`${LOCALE_LABELS[locale] ?? locale}: ${id}`} open onClose={onClose}>
      <DataState loading={detail.loading} error={detail.error} retry={detail.reload}>
        <div className="stack" style={{ gap: 10 }}>
          <p className="muted" style={{ fontSize: 13 }}>
            Түпнұсқа (kk): <strong>{detail.data?.base_title ?? "—"}</strong>
          </p>
          <label className="field">
            <span>Тақырып ({locale})</span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="field">
            <span>Мазмұн (JSON — мәтіндерді аударып, құрылымды сақта)</span>
            <textarea
              className="input"
              rows={12}
              style={{ fontFamily: "ui-monospace, Consolas, monospace", fontSize: 12 }}
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
            />
          </label>
          <div className="inline" style={{ flexWrap: "wrap" }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Статус</span>
              <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
                {statusOptions.map((s2) => (
                  <option key={s2} value={s2}>{s2}</option>
                ))}
              </select>
            </label>
            <div style={{ flex: 1 }} />
            <Button variant="danger" onClick={remove} disabled={busy}>Жою</Button>
            <Button onClick={save} busy={busy}>Сақтау</Button>
          </div>
          {error && <p className="form-error">{error}</p>}
        </div>
      </DataState>
    </Modal>
  );
}

export function AdminPage({
  tab,
}: {
  tab: "scenarios" | "users" | "labs" | "server" | "content" | "translations" | "reports" | "mapping";
}) {
  return (
    <>
      <div className="admin-hero">
        <PageHeading
          eyebrow="УПРАВЛЕНИЕ ПЛАТФОРМОЙ"
          title="Администрирование"
          description="Сценарии, пользователи, сервер и учебные лаборатории."
        />
        <RequireAuth roles={["ADMIN"]}>
          <AdminTabs active={tab} />
        </RequireAuth>
      </div>
      <RequireAuth roles={["ADMIN"]}>
        {tab === "scenarios" ? (
          <ScenariosContent />
        ) : tab === "users" ? (
          <UsersContent />
        ) : tab === "labs" ? (
          <LabsContent />
        ) : tab === "reports" ? (
          <ReportsContent />
        ) : tab === "mapping" ? (
          <MappingContent />
        ) : tab === "server" ? (
          <ServerContent />
        ) : tab === "content" ? (
          <ContentTranslations />
        ) : (
          <TranslationsContent />
        )}
      </RequireAuth>
    </>
  );
}

// ============================================================
// Сервер жағдайы (CPU/RAM/disk/сервистер/контейнерлер/БД)
// ============================================================
interface ServerStatusDto {
  hostname: string;
  platform: string;
  arch: string;
  kernel: string;
  node: string;
  uptime_sec: number;
  load: [number, number, number];
  cpu_count: number;
  memory: { total_gb: number; free_gb: number; used_pct: number };
  disks: { mount: string; total_gb: number; free_gb: number; used_pct: number }[];
  services: { name: string; port: number; up: boolean }[];
  containers: { running: number; total: number } | null;
  database: { file: string; size_mb: number; tables: { name: string; rows: number }[] };
  labs: { running: number; total: number };
  users: { total: number; active: number };
  generated_at: string;
}

function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}к ${h}с ${m}м` : `${h}с ${m}м`;
}

function ServerContent() {
  const [tick, setTick] = useState(0);
  const request = useApi<ServerStatusDto>(`/admin/server-status?t=${tick}`);
  const d = request.data;
  return (
    <DataState {...request} retry={request.reload}>
      {d && (
        <>
          <div className="stat-grid server-cards">
            <div className="panel stat-card">
              <div className="stat-label">ЦОП (CPU)</div>
              <div className="stat-value">
                {d.cpu_count} ядро
                <small> load {d.load[0].toFixed(2)} / {d.load[1].toFixed(2)} / {d.load[2].toFixed(2)}</small>
              </div>
            </div>
            <div className="panel stat-card">
              <div className="stat-label">Жад (RAM)</div>
              <div className="stat-value">{d.memory.free_gb} / {d.memory.total_gb} ГБ бос</div>
              <Progress value={d.memory.used_pct} label="қолданылған" />
            </div>
            {d.disks.map((dk) => (
              <div className="panel stat-card" key={dk.mount}>
                <div className="stat-label">Диск {dk.mount}</div>
                <div className="stat-value">{dk.free_gb} / {dk.total_gb} ГБ бос</div>
                <Progress value={dk.used_pct} label="толған" />
              </div>
            ))}
            <div className="panel stat-card">
              <div className="stat-label">Up-time</div>
              <div className="stat-value">{fmtUptime(d.uptime_sec)}</div>
            </div>
          </div>

          <div className="server-cols">
            <section className="panel section">
              <h2>Сервистер</h2>
              <table>
                <thead>
                  <tr><th>Сервис</th><th>Порт</th><th>Күйі</th></tr>
                </thead>
                <tbody>
                  {d.services.map((sv) => (
                    <tr key={sv.port}>
                      <td><strong>{sv.name}</strong></td>
                      <td>{sv.port}</td>
                      <td>
                        <Badge value={sv.up ? "ACTIVE" : "DISABLED"} /> {sv.up ? "UP" : "DOWN"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {d.containers && (
                <p className="muted" style={{ marginTop: 10 }}>
                  Podman контейнерлері: <strong>{d.containers.running}</strong> іске қосылған / барлығы {d.containers.total}
                </p>
              )}
              <p className="muted">
                {d.hostname} · {d.platform} · {d.arch} · Node {d.node}
              </p>
            </section>

            <section className="panel section">
              <h2>Платформа мәліметтері</h2>
              <table>
                <tbody>
                  <tr><td>Пайдаланушылар</td><td><strong>{d.users.active}</strong> белсенді / {d.users.total}</td></tr>
                  <tr><td>Лабораториялар</td><td><strong>{d.labs.running}</strong> жүріп жатыр / {d.labs.total}</td></tr>
                  <tr><td>БД файл</td><td>{d.database.file} ({d.database.size_mb} MB)</td></tr>
                  <tr><td>Жаңартылды</td><td>{new Date(d.generated_at).toLocaleString("kk-KZ")}</td></tr>
                </tbody>
              </table>
              <Button
                variant="secondary"
                onClick={() => setTick((x) => x + 1)}
                style={{ marginTop: 10 }}
              >
                Жаңарту
              </Button>
            </section>
          </div>

          <section className="panel section">
            <h2>БД кестелері</h2>
            <div className="db-tables">
              {d.database.tables.map((tb) => (
                <div className="db-table-chip" key={tb.name}>
                  <strong>{tb.name}</strong>
                  <span>{tb.rows < 0 ? "—" : tb.rows.toLocaleString()} жол</span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </DataState>
  );
}
function GroupContent({ id }: { id: string }) {
  const request = useApi<GroupAnalytics>(
    `/teacher/groups/${encodeURIComponent(id)}/analytics`,
  );
  const [selected, setSelected] = useState<User>();
  return (
    <DataState {...request} retry={request.reload}>
      {request.data && (
        <>
          <div className="group-heading">
            <Users size={21} />
            <h2>{request.data.name}</h2>
            <span>{request.data.student_count} студентов</span>
          </div>
          <AnalyticsView data={request.data.analytics} />
          <section className="panel section">
            <h2>Сложные сценарии</h2>
            {request.data.difficult_scenarios.length ? (
              <div className="stack">
                {request.data.difficult_scenarios.map((s, i) => (
                  <Progress key={i} value={s.average_score} label={s.title} />
                ))}
              </div>
            ) : (
              <Empty title="Данных пока нет" />
            )}
          </section>
          <section className="panel section">
            <h2>Прогресс студентов</h2>
            {request.data.students.length ? (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Студент</th>
                      <th>Практик</th>
                      <th>Средний балл</th>
                      <th>Прогресс</th>
                      <th>Результаты</th>
                    </tr>
                  </thead>
                  <tbody>
                    {request.data.students.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.completed}</td>
                        <td>{u.average_score}</td>
                        <td>
                          <Progress value={u.progress} />
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            onClick={() => setSelected(u)}
                          >
                            Подробнее
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty title="В группе пока нет студентов" />
            )}
          </section>
          <Modal
            title="Результаты студента"
            open={!!selected}
            onClose={() => setSelected(undefined)}
          >
            {selected && (
              <UserDetails
                key={selected.id}
                user={selected}
                base="/teacher/students"
              />
            )}
          </Modal>
        </>
      )}
    </DataState>
  );
}
function TeacherContent() {
  const groups = useApi<{ id: string; name: string }[]>("/teacher/groups");
  const [group, setGroup] = useState("");
  const [tab, setTab] = useState<"group" | "content">("group");
  const t = useT();
  return (
    <>
      <div className="terminal-tabs" style={{ marginBottom: 16 }}>
        <Button variant={tab === "group" ? "primary" : "ghost"} onClick={() => setTab("group")}>
          {t("teacher.tab_group")}
        </Button>
        <Button variant={tab === "content" ? "primary" : "ghost"} onClick={() => setTab("content")}>
          {t("teacher.tab_content")}
        </Button>
      </div>
      {tab === "content" ? (
        <ContentAnalytics />
      ) : (
        <DataState {...groups} retry={groups.reload}>
          {groups.data?.length ? (
            <>
              <label className="field group-select">
                {t("teacher.group_label")}
                <select
                  value={group || groups.data[0].id}
                  onChange={(e) => setGroup(e.target.value)}
                >
                  {groups.data.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
              <GroupContent
                key={group || groups.data[0].id}
                id={group || groups.data[0].id}
              />
            </>
          ) : (
            <Empty
              title={t("teacher.no_groups")}
              text={t("teacher.no_groups_desc")}
            />
          )}
        </DataState>
      )}
    </>
  );
}

/** PHASE 7: контент аналитикасы — сұрақ сайын сәттілік + флагтар. */
function ContentAnalytics() {
  const t = useT();
  const analytics = useApi<{
    steps: {
      lesson_id: string;
      lesson_title: string;
      step_id: string;
      title: string;
      type: string;
      attempts: number;
      passes: number;
      pass_rate: number;
      auto_flag: string | null;
      teacher_flags: string[];
    }[];
    lessons: {
      id: string;
      title: string;
      course_id: string;
      started: number;
      completed: number;
      completion_rate: number;
    }[];
    flags: {
      id: number;
      lesson_id: string;
      step_id: string;
      reason: string;
      note: string;
      created_at: string;
    }[];
  }>("/teacher/content-analytics");
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const flag = async (lessonId: string, stepId: string, reason: string) => {
    const key = `${lessonId}:${stepId}:${reason}`;
    setBusy(key);
    try {
      await post(`/teacher/content-flags/${encodeURIComponent(lessonId)}`, {
        step_id: stepId,
        reason,
        note: (noteDraft[key] ?? "").slice(0, 500),
      });
      analytics.reload();
    } finally {
      setBusy(null);
    }
  };

  const unflag = async (lessonId: string, stepId: string, reason: string) => {
    try {
      await api(
        `/teacher/content-flags/${encodeURIComponent(lessonId)}/${encodeURIComponent(stepId)}?reason=${reason}`,
        { method: "DELETE" },
      );
      analytics.reload();
    } catch {
      /* ignore */
    }
  };

  const flaggedSteps = analytics.data?.steps.filter((s) => s.auto_flag || s.teacher_flags.length) ?? [];
  const steps = analytics.data?.steps.slice(0, 60) ?? [];

  return (
    <DataState {...analytics} retry={analytics.reload}>
      {analytics.data && analytics.data.steps.length === 0 ? (
        <Empty
          title={t("teacher.content_empty")}
          text={t("teacher.content_empty_desc")}
        />
      ) : (
        <>
          {flaggedSteps.length > 0 && (
            <div className="card" style={{ borderColor: "rgba(244, 63, 94, 0.35)" }}>
              <h3 style={{ marginTop: 0 }}>⚠ {t("teacher.content_flagged")}</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("teacher.content_col_step")}</th>
                    <th>{t("teacher.content_col_attempts")}</th>
                    <th>{t("teacher.content_col_pass")}</th>
                    <th>{t("teacher.content_col_flags")}</th>
                    <th>{t("teacher.content_col_action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {flaggedSteps.map((s) => {
                    const key = `${s.lesson_id}:${s.step_id}`;
                    return (
                      <tr key={key}>
                        <td>
                          <strong>{s.title}</strong>
                          <div className="muted small">
                            {s.lesson_title} · {s.type}
                          </div>
                        </td>
                        <td>{s.attempts}</td>
                        <td>{Math.round(s.pass_rate * 100)}%</td>
                        <td>
                          {[s.auto_flag, ...s.teacher_flags].filter(Boolean).map((f) => (
                            <span key={f} className={`badge badge-warn`}>
                              {t(`teacher.flag_${f}`)}
                            </span>
                          ))}
                        </td>
                        <td>
                          {s.teacher_flags.length > 0 && (
                            <button
                              className="linklike"
                              onClick={() => unflag(s.lesson_id, s.step_id, s.teacher_flags[0])}
                            >
                              {t("teacher.content_unflag")}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="card">
            <h3 style={{ marginTop: 0 }}>{t("teacher.content_lessons")}</h3>
            {analytics.data?.lessons.length === 0 ? (
              <p className="muted">{t("teacher.content_no_lessons")}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("teacher.content_col_lesson")}</th>
                    <th>{t("teacher.content_col_started")}</th>
                    <th>{t("teacher.content_col_completed")}</th>
                    <th>{t("teacher.content_col_rate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.data?.lessons.map((l) => (
                    <tr key={l.id}>
                      <td>{l.title}</td>
                      <td>{l.started}</td>
                      <td>{l.completed}</td>
                      <td>
                        <div className="rate-bar">
                          <div className="rate-fill" style={{ width: `${l.completion_rate * 100}%` }} />
                          <span>{Math.round(l.completion_rate * 100)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>{t("teacher.content_steps")}</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>{t("teacher.content_col_step")}</th>
                  <th>{t("teacher.content_col_attempts")}</th>
                  <th>{t("teacher.content_col_pass")}</th>
                  <th>{t("teacher.content_col_action")}</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((s) => {
                  const key = `${s.lesson_id}:${s.step_id}`;
                  return (
                    <tr key={key}>
                      <td>
                        <strong>{s.title}</strong>
                        <div className="muted small">
                          {s.lesson_title} · {s.type}
                        </div>
                      </td>
                      <td>{s.attempts}</td>
                      <td>
                        <div className="rate-bar">
                          <div
                            className={`rate-fill ${s.pass_rate < 0.4 ? "rate-bad" : s.pass_rate > 0.95 ? "rate-high" : ""}`}
                            style={{ width: `${s.pass_rate * 100}%` }}
                          />
                          <span>{Math.round(s.pass_rate * 100)}%</span>
                        </div>
                      </td>
                      <td className="flag-actions">
                        {(["TOO_HARD", "TOO_EASY", "UNCLEAR"] as const).map((r) => {
                          const active = s.teacher_flags.includes(r);
                          const k = `${key}:${r}`;
                          return (
                            <button
                              key={r}
                              className={`linklike ${active ? "active" : ""}`}
                              disabled={busy === k}
                              onClick={() => (active ? unflag(s.lesson_id, s.step_id, r) : flag(s.lesson_id, s.step_id, r))}
                              title={t(`teacher.flag_${r}`)}
                            >
                              {active ? "✓ " : ""}
                              {t(`teacher.flag_${r}`)}
                            </button>
                          );
                        })}
                        <input
                          className="input flag-note"
                          placeholder={t("teacher.content_note_ph")}
                          value={noteDraft[`${key}:${s.teacher_flags[0] ?? ""}`] ?? ""}
                          onChange={(e) => setNoteDraft((p) => ({ ...p, [`${key}:${s.teacher_flags[0] ?? ""}`]: e.target.value }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </DataState>
  );
}
// ============================================================
// LMS: отчёты для руководства (аттестация, pass-rate, CSV-экспорт)
// ============================================================
interface ReportSummary {
  students: number;
  active_labs: number;
  sessions_done: number;
  sessions_passed: number;
  avg_score: number;
  certificates: number;
}
interface ReportScenario {
  id: string;
  title: string;
  track: string;
  attempts: number;
  passed: number;
  avg_score: number | null;
  pass_rate: number | null;
}
interface ReportTrack {
  track: string;
  attempts: number;
  passed: number;
  pass_rate: number | null;
}
interface ReportUser {
  id: string;
  name: string;
  email: string;
  role: string;
  attempts: number;
  passed: number;
  avg_score: number | null;
  last_activity: string | null;
}
interface ReportsDto {
  summary: ReportSummary;
  scenarios: ReportScenario[];
  tracks: ReportTrack[];
  users: ReportUser[];
}

function ReportsContent() {
  const request = useApi<ReportsDto>("/admin/reports");
  const [downloading, setDownloading] = useState<string | null>(null);
  const exportCsv = async (kind: "users" | "scenarios" | "certificates") => {
    setDownloading(kind);
    try {
      await apiDownload(`/admin/reports?csv=${kind}`, `cyberalem-${kind}.csv`);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setDownloading(null);
    }
  };
  return (
    <DataState {...request} retry={request.reload}>
      {request.data && (
        <>
          <div className="stats-grid">
            <Stat label="Студенты" value={request.data.summary.students} />
            <Stat label="Сессий завершено" value={request.data.summary.sessions_done} />
            <Stat label="Аттестация сдана" value={request.data.summary.sessions_passed} />
            <Stat label="Средний балл" value={`${request.data.summary.avg_score}%`} />
            <Stat label="Сертификатов" value={request.data.summary.certificates} />
            <Stat label="Лабы сейчас" value={request.data.summary.active_labs} />
          </div>
          <div className="inline" style={{ marginBottom: 14 }}>
            <Button variant="secondary" onClick={() => exportCsv("users")} disabled={downloading !== null}>
              {downloading === "users" ? "Экспорт…" : "CSV: студенты"}
            </Button>
            <Button variant="secondary" onClick={() => exportCsv("scenarios")} disabled={downloading !== null}>
              {downloading === "scenarios" ? "Экспорт…" : "CSV: сценарии"}
            </Button>
            <Button variant="secondary" onClick={() => exportCsv("certificates")} disabled={downloading !== null}>
              {downloading === "certificates" ? "Экспорт…" : "CSV: сертификаты"}
            </Button>
          </div>

          <section className="panel table-wrap">
            <h2>По сценариям</h2>
            <table>
              <thead>
                <tr>
                  <th>Сценарий</th>
                  <th>Дорожка</th>
                  <th>Попытки</th>
                  <th>Сдано</th>
                  <th>Средний балл</th>
                  <th>Pass rate</th>
                </tr>
              </thead>
              <tbody>
                {request.data.scenarios.length ? (
                  request.data.scenarios.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <strong>{s.title}</strong>
                      </td>
                      <td>{s.track}</td>
                      <td>{s.attempts}</td>
                      <td>{s.passed}</td>
                      <td>{s.avg_score == null ? "—" : `${s.avg_score}%`}</td>
                      <td>{s.pass_rate == null ? "—" : `${s.pass_rate}%`}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="muted">
                      Пока нет завершённых практик.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <section className="panel table-wrap">
            <h2>По дорожкам (трекам)</h2>
            <table>
              <thead>
                <tr>
                  <th>Дорожка</th>
                  <th>Попытки</th>
                  <th>Сдано</th>
                  <th>Pass rate</th>
                </tr>
              </thead>
              <tbody>
                {request.data.tracks.length ? (
                  request.data.tracks.map((t) => (
                    <tr key={t.track}>
                      <td>
                        <strong>{t.track}</strong>
                      </td>
                      <td>{t.attempts}</td>
                      <td>{t.passed}</td>
                      <td>{t.pass_rate == null ? "—" : `${t.pass_rate}%`}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="muted">
                      Пока нет данных.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <section className="panel table-wrap">
            <h2>Студенты (активность)</h2>
            <table>
              <thead>
                <tr>
                  <th>ФИО</th>
                  <th>Email</th>
                  <th>Попытки</th>
                  <th>Сдано</th>
                  <th>Средний балл</th>
                  <th>Последняя активность</th>
                </tr>
              </thead>
              <tbody>
                {request.data.users.length ? (
                  request.data.users.slice(0, 100).map((u) => (
                    <tr key={u.id}>
                      <td>
                        <strong>{u.name}</strong>
                      </td>
                      <td className="muted">{u.email}</td>
                      <td>{u.attempts}</td>
                      <td>{u.passed}</td>
                      <td>{u.avg_score == null ? "—" : `${u.avg_score}%`}</td>
                      <td>{u.last_activity ? date(u.last_activity) : "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="muted">
                      Пока нет активности.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </>
      )}
    </DataState>
  );
}

/* ---------------- F5: матрица соответствия стандартам ---------------- */
interface MappingDto {
  mitre: Record<string, string>;
  kz: Record<string, string>;
  scenarios: {
    id: string;
    title: string;
    tasks: { id: string; title: string; mapping?: { mitre?: string[]; kz?: string[] } }[];
  }[];
}

function MappingContent() {
  const request = useApi<MappingDto>("/admin/mapping");
  return (
    <DataState {...request} retry={request.reload}>
      {request.data && (
        <>
          <p className="muted" style={{ marginBottom: 14 }}>
            Соответствие каждой практики сценария техникам MITRE ATT&CK и
            нормативной базе РК. Матрица используется в отчётах для руководства
            и при аккредитации программы.
          </p>
          {request.data.scenarios.map((sc) => (
            <section className="panel table-wrap" key={sc.id} style={{ marginBottom: 16 }}>
              <h2>{sc.title}</h2>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "35%" }}>Задача</th>
                    <th>MITRE ATT&CK</th>
                    <th>Нормативная база РК</th>
                  </tr>
                </thead>
                <tbody>
                  {sc.tasks.map((task) => (
                    <tr key={task.id}>
                      <td>
                        <span className="muted">{task.id}</span> {task.title}
                      </td>
                      <td>
                        <div className="mapping-chips">
                          {(task.mapping?.mitre ?? []).length ? (
                            (task.mapping?.mitre ?? []).map((id) => (
                              <span key={id} className="chip chip-mitre" title={id}>
                                {request.data!.mitre[id] ?? id}
                              </span>
                            ))
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="mapping-chips">
                          {(task.mapping?.kz ?? []).length ? (
                            (task.mapping?.kz ?? []).map((id) => (
                              <span key={id} className="chip chip-kz" title={request.data!.kz[id]}>
                                {KZ_SHORT[id] ?? id}
                              </span>
                            ))
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </>
      )}
    </DataState>
  );
}

export function TeacherPage() {
  const t = useT();
  return (
    <>
      <PageHeading
        eyebrow="ПРЕПОДАВАТЕЛЬ"
        title={t("teacher.page_title")}
        description={t("teacher.page_desc")}
      />
      <RequireAuth roles={["TEACHER", "ADMIN"]}>
        <TeacherContent />
      </RequireAuth>
    </>
  );
}
